/**
 * W66.W6 — Cadence bundle: 3 columns (HR / Finance / Operations), 6 rows each (Touches 1-6).
 * Each cell: touch number + format chip, discussion-control move (italic),
 *   full pre-written copy (subject bold, body monospace), pre-empts chips.
 */

import React, { useState } from "react";

interface CadenceTouch {
  touch: number;
  format: string;
  dc_move: string;
  subject: string;
  body: string;
  pre_empts: string[];
}

interface CadenceBundleProps {
  planData: Record<string, any>;
}

const FORMAT_STYLES: Record<string, { bg: string; color: string }> = {
  EMAIL: { bg: "#EFF6FF", color: "#0B0B0F" },
  "PHONE NO VM": { bg: "#F0FDF4", color: "#15803D" },
  "EMAIL FOLLOW-UP": { bg: "#EFF6FF", color: "#0B0B0F" },
  LINKEDIN: { bg: "#F5F3FF", color: "#7C3AED" },
  "PHONE+VM+EMAIL": { bg: "#FFF7ED", color: "#EA580C" },
  "LETTER+TRINKET": { bg: "#FEFCE8", color: "#A16207" },
};

const PREEMPT_STYLES: Record<string, { bg: string; color: string }> = {
  PRICE: { bg: "#FFF7ED", color: "#EA580C" },
  TIMING: { bg: "#FEFCE8", color: "#CA8A04" },
  AUTHORITY: { bg: "#FEF2F2", color: "#DC2626" },
};

function FormatChip({ format }: { format: string }) {
  const s = FORMAT_STYLES[format.toUpperCase()] ?? { bg: "#F3F4F6", color: "#374151" };
  return (
    <span
      style={{
        background: s.bg,
        color: s.color,
        borderRadius: 4,
        padding: "2px 7px",
        fontSize: 10,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        whiteSpace: "nowrap",
      }}
    >
      {format}
    </span>
  );
}

function PreEmptChip({ label }: { label: string }) {
  const s = PREEMPT_STYLES[label.toUpperCase()] ?? { bg: "#F3F4F6", color: "#374151" };
  return (
    <span
      style={{
        background: s.bg,
        color: s.color,
        borderRadius: 3,
        padding: "1px 6px",
        fontSize: 10,
        fontWeight: 600,
        textTransform: "uppercase",
      }}
    >
      {label}
    </span>
  );
}

function TouchCell({ touch }: { touch: CadenceTouch }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #E5E7EB",
        borderRadius: 8,
        overflow: "hidden",
        marginBottom: 8,
      }}
    >
      {/* Touch header */}
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
          padding: "8px 12px",
          background: "#F9FAFB",
          border: "none",
          borderBottom: expanded ? "1px solid #E5E7EB" : "none",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#6B7280",
            minWidth: 64,
          }}
        >
          Touch {touch.touch}
        </span>
        <FormatChip format={touch.format} />
        <span
          style={{
            fontSize: 10,
            color: "#9CA3AF",
            marginLeft: "auto",
          }}
        >
          {expanded ? "\u25B2" : "\u25BC"}
        </span>
      </button>

      {expanded && (
        <div style={{ padding: "10px 12px" }}>
          {/* Discussion control move */}
          {touch.dc_move && (
            <p
              style={{
                fontSize: 12,
                color: "#374151",
                fontStyle: "italic",
                margin: "0 0 8px",
                lineHeight: 1.5,
              }}
            >
              {touch.dc_move}
            </p>
          )}

          {/* Subject */}
          {touch.subject && touch.subject !== "Phone \u2014 no voicemail" && touch.subject !== "Phone" && (
            <div style={{ marginBottom: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#111827" }}>
                Subject: {touch.subject}
              </span>
            </div>
          )}

          {/* Body */}
          {touch.body && (
            <pre
              style={{
                fontSize: 11,
                color: "#374151",
                lineHeight: 1.6,
                background: "#F9FAFB",
                border: "1px solid #E5E7EB",
                borderRadius: 4,
                padding: "8px 10px",
                margin: "0 0 8px",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: "inherit",
              }}
            >
              {touch.body}
            </pre>
          )}

          {/* Pre-empts */}
          {touch.pre_empts?.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
              <span style={{ fontSize: 10, color: "#9CA3AF", fontWeight: 600 }}>
                Pre-empts:
              </span>
              {touch.pre_empts.map((p, i) => (
                <PreEmptChip key={i} label={p} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CadenceBundle({ planData }: CadenceBundleProps) {
  const cadence = planData.cadence ?? {};
  const personas = ["HR", "Finance", "Operations"];

  const hasData = personas.some((p) => (cadence[p] ?? []).length > 0);
  if (!hasData) return null;

  return (
    <section style={{ marginBottom: 28 }}>
      <h2
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: "#374151",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          margin: "0 0 10px",
          borderBottom: "1px solid #E5E7EB",
          paddingBottom: 6,
        }}
      >
        Rapid Cadence Pack
      </h2>

      <p style={{ fontSize: 12, color: "#6B7280", margin: "0 0 16px" }}>
        Three personas \u00d7 six touches = eighteen outreach blocks. Each touch carries the
        discussion-control move and the objection it pre-empts. Click any touch to expand the full copy.
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 16,
        }}
      >
        {personas.map((persona) => {
          const touches: CadenceTouch[] = cadence[persona] ?? [];
          return (
            <div key={persona}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#111827",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  marginBottom: 8,
                  padding: "6px 0",
                  borderBottom: "2px solid #111827",
                }}
              >
                {persona}
              </div>
              {touches.length > 0 ? (
                touches.map((t) => <TouchCell key={t.touch} touch={t} />)
              ) : (
                <div style={{ fontSize: 12, color: "#9CA3AF", fontStyle: "italic" }}>
                  No cadence data yet.
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
